import React, { useState } from "react";
import { useToast } from "../../../../context/ToastContext";
import { formatDateForInput } from "../../../../utils/formatters";
import { RELATIONSHIP_OPTIONS } from "../../../../constants/membersConstants";
import {
  getHtmlDateMinMax,
  normalizeToYmd,
  validatePastOnlyYmd,
} from "../../../../utils/dateFieldConstraints";
import DatePickerInput from "../DatePickerInput";
import MemberSelect from "../../../shared/MemberSelect";

const isMissing = (val) => val === null || val === undefined || val === "";

const InheritanceInfoForm = ({ householdData, fieldData, entityId, onSave, onSaveAndExit, saving, nextLabel }) => {
  const { toast } = useToast();
  const account = fieldData || {};
  const existing = account.inheritance_info || {};
  const members = householdData?.members || [];

  const needDecedent = isMissing(existing.decedent_info?.id);
  const needRelationship = isMissing(existing.decedent_info?.relationship);

  const getMemberById = (id) =>
    members.find((m) => String(m.id) === String(id));

  const initialDecedentId = needDecedent ? "" : existing.decedent_info?.id || "";
  const initialDecedent = getMemberById(initialDecedentId);

  const [formData, setFormData] = useState({
    decedent_id: "",
    relationship: "",
    is_deceased: initialDecedent?.personal_info?.is_deceased !== undefined
      ? String(initialDecedent.personal_info.is_deceased)
      : "",
    date_of_death: formatDateForInput(initialDecedent?.personal_info?.date_of_death),
  });

  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const missingCount = (needDecedent ? 1 : 0) + (needRelationship ? 1 : 0);

  if (missingCount === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All fields have been filled.</p>
      </div>
    );
  }

  const currentDecedentId = needDecedent ? formData.decedent_id : existing.decedent_info?.id;
  const pastBounds = getHtmlDateMinMax("past_only");

  const buildPayload = () => {
    const decId = needDecedent ? formData.decedent_id : existing.decedent_info?.id;
    const payload = {
      accounts: {
        [entityId]: {
          inheritance_info: {
            decedent_info: {
              id: decId,
              relationship: needRelationship
                ? formData.relationship || null
                : existing.decedent_info?.relationship || null,
            },
          },
        },
      },
    };
    if (decId && (formData.is_deceased !== "" || formData.date_of_death)) {
      payload.household_members = {
        [decId]: {
          personal_info: {
            ...(formData.is_deceased !== "" ? { is_deceased: formData.is_deceased === "true" } : {}),
            ...(formData.date_of_death ? { date_of_death: formData.date_of_death } : {}),
          },
        },
      };
    }
    return payload;
  };

  const validate = () => {
    if (needDecedent && !formData.decedent_id) return "Please select the decedent.";
    if (needRelationship && !formData.relationship) return "Owner's Relationship to Decedent is required.";
    if (formData.date_of_death) {
      const msg = validatePastOnlyYmd(normalizeToYmd(formData.date_of_death), "Date of death");
      if (msg) return msg;
    }
    return null;
  };

  const handleSubmit = (saveCallback) => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    saveCallback(buildPayload());
  };

  const inputCls =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          ● {missingCount} Required field{missingCount !== 1 ? "s" : ""} missing
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {needDecedent && (
          <div>
            <MemberSelect
              label="Decedent"
              required
              members={members}
              value={formData.decedent_id}
              onChange={(id) => {
                const m = getMemberById(id);
                setFormData((p) => ({
                  ...p,
                  decedent_id: id,
                  is_deceased: m?.personal_info?.is_deceased !== undefined
                    ? String(m.personal_info.is_deceased)
                    : "",
                  date_of_death: formatDateForInput(m?.personal_info?.date_of_death),
                }));
              }}
            />
          </div>
        )}
        {needRelationship && (
          <div>
            <label className={labelCls}>Owner's Relationship to Decedent <span className="text-red-500">*</span></label>
            <select value={formData.relationship} onChange={(e) => set("relationship", e.target.value)} className={inputCls}>
              <option value="">Select...</option>
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        )}
        {currentDecedentId && (
          <>
            <div>
              <label className={labelCls}>Is the decedent deceased?</label>
              <select value={formData.is_deceased} onChange={(e) => set("is_deceased", e.target.value)} className={inputCls}>
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Date of Death</label>
              <DatePickerInput
                value={formData.date_of_death}
                onChange={(e) => set("date_of_death", e.target.value)}
                className={inputCls}
                min={pastBounds.min}
                max={pastBounds.max}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
          Save & Exit
        </button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default InheritanceInfoForm;
